/**
 * BlockSplitter — string-level block scanner shared across surfaces.
 *
 * Lifted from the edge proxy's `translate-html.ts` scanner (webhook Phase 10)
 * so non-DOM surfaces (edge Worker, webhook Worker) split HTML into the SAME
 * block boundaries instead of hand-rolling a second parser. The browser DOM
 * path (`browser/block-extractor.ts`) mirrors the same tag sets via
 * `querySelectorAll`.
 *
 * Structural only: finds innermost block elements (p, h1–h6, li, td, …) and
 * orphan inline `<a>` outside any block, returning each block's opening tag,
 * raw attribute string, and byte-exact innerHTML with source offsets. No
 * keying, no exclude filtering, no translation — callers layer their own
 * policy (edge: data-i18n precedence + ExcludeFilter + replacement stamping;
 * webhook P13: `toContentUnit(inner)` + `generateAutoKey` per block). Blocks
 * inside skip tags (`<script>`/`<style>`/`<pre>`/…), HTML comments, or
 * `data-no-translate` / `translate="no"` regions are not emitted.
 *
 * Pure string ops — Worker-safe and Node-safe (no DOM, no Node APIs).
 *
 * @module content/splitter/BlockSplitter
 */

export interface HtmlBlock {
  /** Lowercased tag name (`p`, `h2`, `a`, …). */
  tag: string;
  /** The exact opening-tag source text, e.g. `<p class="intro">`. */
  openTag: string;
  /** Raw attribute string from the opening tag (verbatim, may be empty). */
  attrs: string;
  /** The block's innerHTML, byte-exact as it appears in the input. */
  inner: string;
  /** Offset of `inner` within the input html. */
  innerStart: number;
  /** Offset just past `inner` — the `<` of the matching closing tag. */
  innerEnd: number;
}

export interface SplitBlocksResult {
  /** Captured blocks in document order; ranges never overlap. */
  blocks: HtmlBlock[];
}

const BLOCK_TAGS = new Set([
  'title', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'li', 'td', 'th', 'label', 'button', 'figcaption',
  'dt', 'dd', 'summary', 'caption',
]);

// Inline elements eligible for "orphan" extraction. The scanner jumps past
// every captured block via findCloseTag, so any <a> the main loop visits has
// no captured-block parent (e.g. <div><a>X</a></div>). Conservative on
// purpose: only <a> — mirrors browser INLINE_BLOCK_SELECTOR.
const INLINE_BLOCK_TAGS = new Set(['a']);

const SKIP_TAGS = new Set([
  'script', 'style', 'code', 'pre', 'textarea', 'noscript', 'svg',
]);

/** Tag regex — matches <tag attrs>, </tag>, <tag/> with quoted attributes */
const TAG_REGEX =
  /<(\/?)([a-zA-Z][a-zA-Z0-9]*)((?:\s+[a-zA-Z][a-zA-Z0-9-]*(?:=(?:"[^"]*"|'[^']*'|[^\s>]*))?)*)\s*(\/?)>/g;

/**
 * Check if an attribute string contains data-no-translate or translate="no"
 */
function hasNoTranslateAttr(attrsString: string): boolean {
  if (attrsString.includes('data-no-translate')) return true;
  return /translate\s*=\s*["']no["']/.test(attrsString);
}

/**
 * Check if innerHTML contains a block-level opening tag
 */
function containsBlockTag(innerHTML: string): boolean {
  TAG_REGEX.lastIndex = 0;
  let match;
  while ((match = TAG_REGEX.exec(innerHTML)) !== null) {
    const [, closingSlash, tagName, , selfClosingSlash] = match;
    if (!closingSlash && !selfClosingSlash && BLOCK_TAGS.has(tagName.toLowerCase())) {
      return true;
    }
  }
  return false;
}

/**
 * Find the matching close tag for a given tag name starting from pos.
 * Returns the index of the '<' of the closing tag, or -1 if not found.
 */
export function findCloseTag(html: string, pos: number, tagName: string): number {
  const lowerTag = tagName.toLowerCase();
  let depth = 1;
  TAG_REGEX.lastIndex = pos;

  let match;
  while ((match = TAG_REGEX.exec(html)) !== null) {
    const [, closingSlash, matchedTag, , selfClosingSlash] = match;
    if (matchedTag.toLowerCase() !== lowerTag) continue;

    if (closingSlash) {
      depth--;
      if (depth === 0) return match.index;
    } else if (!selfClosingSlash) {
      depth++;
    }
  }

  return -1;
}

/**
 * Split an HTML string into its translatable block elements.
 *
 * Walks the string once; each captured block is innermost (an outer block
 * whose innerHTML contains another block tag is skipped in favor of the
 * inner one) and non-overlapping, so replacing inners via
 * `replaceBlockInners` is always position-safe.
 */
export function splitBlocks(html: string): SplitBlocksResult {
  const blocks: HtmlBlock[] = [];

  let pos = 0;
  let skipDepth = 0;
  let iter = 0;
  const maxIter = 100_000;

  // Stack of byte-positions where each open no-translate region ends.
  // Pushed when we encounter `<TAG data-no-translate>` (any element);
  // drained when `pos` reaches/passes the recorded end. While the stack
  // is non-empty, block + orphan-inline extraction is suppressed.
  const noTranslateEnds: number[] = [];

  while (pos < html.length && iter < maxIter) {
    iter++;

    const ltPos = html.indexOf('<', pos);
    if (ltPos === -1) break;

    // Check for HTML comment
    if (html.startsWith('<!--', ltPos)) {
      const commentEnd = html.indexOf('-->', ltPos + 4);
      if (commentEnd === -1) break; // unclosed comment, stop
      pos = commentEnd + 3;
      continue;
    }

    // Try to match a tag at this position
    TAG_REGEX.lastIndex = ltPos;
    const match = TAG_REGEX.exec(html);

    // If no match or match isn't at ltPos, skip past this '<'
    if (!match || match.index !== ltPos) {
      pos = ltPos + 1;
      continue;
    }

    const [fullMatch, closingSlash, tagName, attrsString, selfClosingSlash] = match;
    const tag = tagName.toLowerCase();
    const afterMatch = match.index + fullMatch.length;

    // Handle skip tags
    if (SKIP_TAGS.has(tag)) {
      if (closingSlash) {
        if (skipDepth > 0) skipDepth--;
      } else if (!selfClosingSlash) {
        skipDepth++;
      }
      pos = afterMatch;
      continue;
    }

    // Inside skip region
    if (skipDepth > 0) {
      pos = afterMatch;
      continue;
    }

    // Drain no-translate ranges we've exited (pos beyond the recorded end).
    while (
      noTranslateEnds.length &&
      pos >= noTranslateEnds[noTranslateEnds.length - 1]
    ) {
      noTranslateEnds.pop();
    }

    // Open a new no-translate region when this tag carries the attribute
    // and isn't self-closing. Record its END position (the `<` of `</TAG>`)
    // so the drain loop above can pop us out cleanly.
    if (!closingSlash && !selfClosingSlash && hasNoTranslateAttr(attrsString)) {
      const regionEnd = findCloseTag(html, afterMatch, tagName);
      if (regionEnd !== -1) {
        noTranslateEnds.push(regionEnd);
      }
    }

    // Inside a no-translate region (including the tag that opened it) —
    // suppress extraction. We still advance through tags so the stack
    // drains correctly when we exit.
    if (noTranslateEnds.length > 0) {
      pos = afterMatch;
      continue;
    }

    // Block tags + orphan inline <a>: same capture logic. Any <a> reaching
    // here has no captured-block ancestor — the scanner jumps past every
    // captured block's closing tag.
    if (
      (BLOCK_TAGS.has(tag) || INLINE_BLOCK_TAGS.has(tag)) &&
      !closingSlash &&
      !selfClosingSlash
    ) {
      // An unclosed no-translate tag lands here (no region was pushed);
      // findCloseTag returns the same -1 → skipped like any malformed block.
      const closeTagStart = findCloseTag(html, afterMatch, tagName);
      if (closeTagStart === -1) {
        // Malformed — skip
        pos = afterMatch;
        continue;
      }

      const innerHTML = html.slice(afterMatch, closeTagStart);

      // If innerHTML contains nested block tags, skip this outer element
      // and let the scanner extract the inner blocks
      if (containsBlockTag(innerHTML)) {
        pos = afterMatch;
        continue;
      }

      blocks.push({
        tag,
        openTag: fullMatch,
        attrs: attrsString,
        inner: innerHTML,
        innerStart: afterMatch,
        innerEnd: closeTagStart,
      });

      // Skip past the closing tag
      TAG_REGEX.lastIndex = closeTagStart;
      const closeMatch = TAG_REGEX.exec(html);
      pos = closeMatch ? closeMatch.index + closeMatch[0].length : closeTagStart + 1;
      continue;
    }

    pos = afterMatch;
  }

  return { blocks };
}

/**
 * Splice replacement innerHTML into blocks previously returned by
 * `splitBlocks(html)` for the SAME html string. Blocks not listed keep
 * their source content; wrapper tags and attributes are never touched.
 *
 * Applied end-to-start so earlier offsets stay valid.
 */
export function replaceBlockInners(
  html: string,
  replacements: ReadonlyArray<{ block: HtmlBlock; inner: string }>
): string {
  const ordered = [...replacements].sort((a, b) => b.block.innerStart - a.block.innerStart);
  let result = html;
  for (const { block, inner } of ordered) {
    result = result.slice(0, block.innerStart) + inner + result.slice(block.innerEnd);
  }
  return result;
}
